import React, { useState, useEffect } from "react";
import {
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableHead,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Divider,
  Grid,
  Typography,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../customComponents/styled/styledTable";
import {
  useGetInvoiceQuery,
  useConfirmInvoiceMutation,
  useLazyGetClassroomFeesQuery,
  useAddInvoiceItemsMutation,
  useAddInvoiceItemMutation,
  useEditInvoiceItemMutation,
  useDeleteInvoiceItemMutation,
} from "./invoicesApiSlice";
import { useRemoveInvoiceDiscountMutation } from "./discountApiSlice";
import { useGetSchoolProfileQuery } from "../settings/school/schoolApiSlice";
import { downloadInvoiceReceiptPdf } from "./receiptUtils";

function EvidencePreviewModal({ url, open, onClose }) {
  const googleDocsUrl = url
    ? `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`
    : null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        Payment Evidence
        <IconButton size="small" onClick={onClose}><CloseSharpIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, height: "80vh" }}>
        {googleDocsUrl && (
          <iframe src={googleDocsUrl} title="Evidence Preview" width="100%" height="100%" style={{ border: "none" }} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function InvoiceDialog({ open, setOpen, selectedInvoice }) {
  const [amountPaid, setAmountPaid] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFeeIds, setSelectedFeeIds] = useState([]);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editValues, setEditValues] = useState({ name: "", amount: "" });
  const [customName, setCustomName] = useState("");
  const [customAmount, setCustomAmount] = useState("");

  // The list page only hands us a snapshot of the invoice from whenever it
  // was fetched for the table. Subscribe to the live invoice here too, so
  // adding/editing/deleting an item (or confirming payment, or removing a
  // discount) shows up immediately instead of requiring a close/reopen.
  const { data: liveInvoice } = useGetInvoiceQuery(`/${selectedInvoice?.id}`, {
    skip: !open || !selectedInvoice?.id,
  });
  const invoice = liveInvoice || selectedInvoice;

  const [confirmInvoice, { isLoading: confirming }] = useConfirmInvoiceMutation();
  const [removeDiscount, { isLoading: removingDiscount }] = useRemoveInvoiceDiscountMutation();
  const [addInvoiceItems, { isLoading: addingItems }] = useAddInvoiceItemsMutation();
  const [addInvoiceItem, { isLoading: addingCustomItem }] = useAddInvoiceItemMutation();
  const [editInvoiceItem, { isLoading: editingItem }] = useEditInvoiceItemMutation();
  const [deleteInvoiceItem, { isLoading: deletingItem }] = useDeleteInvoiceItemMutation();
  const [fetchClassroomFees, { data: classroomFees, isFetching: fetchingFees }] = useLazyGetClassroomFeesQuery();
  const { data: schoolProfile } = useGetSchoolProfileQuery();

  const classroomId = invoice?.classroom?.id;
  const studentGender = invoice?.student?.gender;
  // Items are owned by the invoice now — a catalog fee counts as "already on
  // this invoice" via the item's fee_id back-reference, not the item's own id.
  const existingFeeIds = new Set((invoice?.items || []).map((i) => i.fee_id).filter(Boolean));
  const missingFees = (classroomFees || []).filter(
    (f) => !existingFeeIds.has(f.id) && (!f.gender || f.gender === studentGender)
  );

  useEffect(() => {
    if (open && classroomId) {
      fetchClassroomFees(classroomId);
    }
  }, [open, classroomId, fetchClassroomFees]);

  const handleClose = () => {
    setOpen(false);
    setAmountPaid("");
    setPreviewUrl(null);
    setSelectedFeeIds([]);
    setEditingItemId(null);
    setCustomName("");
    setCustomAmount("");
  };

  const toggleFee = (id) => {
    setSelectedFeeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAddItems = async () => {
    if (!selectedFeeIds.length) return;
    try {
      await addInvoiceItems({ invoiceId: invoice.id, items: selectedFeeIds }).unwrap();
      setSelectedFeeIds([]);
    } catch {
      alert("Failed to add fee items. Please try again.");
    }
  };

  const handleAddCustomItem = async () => {
    if (!customName.trim() || !customAmount || Number(customAmount) <= 0) return;
    try {
      await addInvoiceItem({
        invoiceId: invoice.id,
        name: customName.trim(),
        amount: Number(customAmount),
      }).unwrap();
      setCustomName("");
      setCustomAmount("");
    } catch (error) {
      alert(error?.data?.message || "Failed to add custom item. Please try again.");
    }
  };

  const startEditingItem = (item) => {
    setEditingItemId(item.id);
    setEditValues({ name: item.name, amount: item.amount });
  };

  const handleSaveItem = async (item) => {
    try {
      await editInvoiceItem({
        invoiceId: invoice.id,
        itemId: item.id,
        name: editValues.name,
        amount: Number(editValues.amount),
      }).unwrap();
      setEditingItemId(null);
    } catch (error) {
      alert(error?.data?.message || "Failed to update item. Please try again.");
    }
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Remove "${item.name}" from this invoice?`)) return;
    try {
      await deleteInvoiceItem({ invoiceId: invoice.id, itemId: item.id }).unwrap();
    } catch (error) {
      alert(error?.data?.message || "Failed to remove item. Please try again.");
    }
  };

  const handleRemoveDiscount = async () => {
    if (!window.confirm("Remove the discount from this invoice?")) return;
    try {
      await removeDiscount(invoice.id).unwrap();
    } catch {
      alert("Failed to remove discount. Please try again.");
    }
  };

  const handleConfirm = async (fullPay) => {
    const total = invoice?.total_amount ?? invoice?.items?.reduce((s, i) => s + (i.amount || 0), 0) ?? 0;
    const discAmt = invoice?.discount_amount ?? invoice?.invoice_discount?.applied_amount ?? 0;
    const netTotal = Math.max(0, total - discAmt);
    const value = fullPay ? netTotal : Number(amountPaid);
    if (!value || value <= 0) return;
    try {
      await confirmInvoice({ invoiceId: invoice.id, amount_paid: value }).unwrap();
      handleClose();
    } catch {
      alert("Failed to confirm invoice. Please try again.");
    }
  };

  const handleDownloadReceipt = () => {
    try {
      downloadInvoiceReceiptPdf(invoice, schoolProfile);
    } catch {
      alert("Failed to download receipt. Please try again.");
    }
  };

  if (!selectedInvoice) return null;

  const isPaid = invoice.paid;
  const isPartial = invoice.partial_paid && !invoice.paid;
  const totalAmount = invoice.total_amount ?? invoice.items?.reduce((s, i) => s + (i.amount || 0), 0) ?? 0;
  const amountPaidVal = invoice.amount_paid || 0;
  const discountAmount = invoice.discount_amount ?? invoice.invoice_discount?.applied_amount ?? 0;
  const amountDue = invoice.amount_due ?? Math.max(0, totalAmount - discountAmount - amountPaidVal);
  const statusLabel = isPaid ? "Paid" : isPartial ? "Partially Paid" : "Unpaid";
  const statusColor = isPaid ? "success" : isPartial ? "warning" : "error";
  const invoiceDiscount = invoice.invoice_discount ?? null;
  const canDownloadReceipt = isPaid || isPartial;

  const student = invoice.student;
  const studentName = student ? `${student.first_name || ""} ${student.last_name || ""}`.trim() : "—";

  return (
    <Dialog onClose={handleClose} open={open} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h6">{studentName}</Typography>
          <Chip label={statusLabel} color={statusColor} size="small" />
          {invoice.term?.name && (
            <Typography variant="body2" color="text.secondary">· {invoice.term.name}</Typography>
          )}
          {invoice.session?.name && (
            <Typography variant="body2" color="text.secondary">· {invoice.session.name}</Typography>
          )}
          {invoice.classroom?.name && (
            <Typography variant="body2" color="text.secondary">· {invoice.classroom.name}</Typography>
          )}
        </Box>
        <IconButton size="small" onClick={handleClose}><CloseSharpIcon /></IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {/* Amount summary */}
        <Box sx={{ display: "flex", gap: 3, mb: 3, flexWrap: "wrap" }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Total</Typography>
            <Typography fontWeight="bold">₦{totalAmount.toLocaleString()}</Typography>
          </Box>
          {discountAmount > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">Discount</Typography>
              <Typography fontWeight="bold" color="info.main">−₦{discountAmount.toLocaleString()}</Typography>
            </Box>
          )}
          {amountPaidVal > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">Amount Paid</Typography>
              <Typography fontWeight="bold" color="success.main">₦{amountPaidVal.toLocaleString()}</Typography>
            </Box>
          )}
          {!isPaid && (
            <Box>
              <Typography variant="caption" color="text.secondary">Balance Due</Typography>
              <Typography fontWeight="bold" color="error.main">₦{amountDue.toLocaleString()}</Typography>
            </Box>
          )}
          {canDownloadReceipt && (
            <Box sx={{ ml: "auto", alignSelf: "center" }}>
              <Tooltip title="Download payment receipt">
                <Button
                  variant="outlined"
                  startIcon={<DownloadOutlinedIcon />}
                  sx={{ textTransform: "none" }}
                  onClick={handleDownloadReceipt}
                >
                  Download Receipt
                </Button>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Discount badge */}
        {invoiceDiscount && (
          <Box sx={{ mb: 2, p: 1.5, border: "1px solid", borderColor: "info.light", borderRadius: 1, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Chip label="Discount Applied" color="info" size="small" />
              <Typography variant="body2" fontWeight="medium">
                {invoiceDiscount.discount?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                −₦{discountAmount.toLocaleString()}
              </Typography>
              {invoiceDiscount.note && (
                <Typography variant="caption" color="text.secondary">· {invoiceDiscount.note}</Typography>
              )}
            </Box>
            <Button
              size="small"
              color="error"
              variant="outlined"
              disabled={removingDiscount}
              onClick={handleRemoveDiscount}
            >
              Remove Discount
            </Button>
          </Box>
        )}

        <Grid container spacing={2}>
          {/* Items */}
          <Grid item xs={12}>
            <StyledPaper>
              <Box sx={{ padding: "12px 15px" }}>
                <Typography variant="formSubHeading" sx={{ textTransform: "none" }}>
                  Invoice Items
                </Typography>
              </Box>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ borderBottom: "1.5px solid lightgrey" }}>
                      <StyledTableCell>Name</StyledTableCell>
                      <StyledTableCell>Amount</StyledTableCell>
                      <StyledTableCell>Type</StyledTableCell>
                      <StyledTableCell>Actions</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoice.items?.map((item) =>
                      editingItemId === item.id ? (
                        <StyledTableRow key={item.id}>
                          <StyledTableCell>
                            <TextField
                              size="small"
                              value={editValues.name}
                              onChange={(e) => setEditValues((v) => ({ ...v, name: e.target.value }))}
                            />
                          </StyledTableCell>
                          <StyledTableCell>
                            <TextField
                              size="small"
                              type="number"
                              value={editValues.amount}
                              onChange={(e) => setEditValues((v) => ({ ...v, amount: e.target.value }))}
                            />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Chip
                              label={item.is_compulsory ? "Compulsory" : "Optional"}
                              size="small"
                              variant="outlined"
                            />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              {editingItem ? (
                                <CircularProgress size={18} />
                              ) : (
                                <IconButton size="small" onClick={() => handleSaveItem(item)}>
                                  <SaveIcon fontSize="small" />
                                </IconButton>
                              )}
                              <IconButton size="small" onClick={() => setEditingItemId(null)}>
                                <CloseSharpIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </StyledTableCell>
                        </StyledTableRow>
                      ) : (
                        <StyledTableRow key={item.id}>
                          <StyledTableCell>
                            {item.name}
                            {item.is_custom && (
                              <Chip label="Custom" size="small" variant="outlined" color="secondary" sx={{ ml: 1 }} />
                            )}
                          </StyledTableCell>
                          <StyledTableCell>₦{(item.amount || 0).toLocaleString()}</StyledTableCell>
                          <StyledTableCell>
                            <Chip
                              label={item.is_compulsory ? "Compulsory" : "Optional"}
                              size="small"
                              variant="outlined"
                            />
                          </StyledTableCell>
                          <StyledTableCell>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <IconButton size="small" onClick={() => startEditingItem(item)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              {deletingItem ? (
                                <CircularProgress size={18} />
                              ) : (
                                <IconButton size="small" onClick={() => handleDeleteItem(item)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                          </StyledTableCell>
                        </StyledTableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ p: 2, display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", borderTop: "1px solid", borderColor: "divider" }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  Add custom item:
                </Typography>
                <TextField
                  size="small"
                  placeholder="e.g. Outstanding balance from 2024"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  sx={{ minWidth: 220 }}
                />
                <TextField
                  size="small"
                  type="number"
                  placeholder="Amount"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  sx={{ width: 140 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddCircleOutlineIcon />}
                  disabled={addingCustomItem || !customName.trim() || !customAmount}
                  onClick={handleAddCustomItem}
                >
                  Add
                </Button>
              </Box>
            </StyledPaper>
          </Grid>

          {/* Evidences */}
          {invoice.evidences?.length > 0 && (
            <Grid item xs={12}>
              <StyledPaper>
                <Box sx={{ padding: "12px 15px" }}>
                  <Typography variant="formSubHeading" sx={{ textTransform: "none" }}>
                    Payment Evidences ({invoice.evidences.length})
                  </Typography>
                </Box>
                <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                  {invoice.evidences.map((ev) => (
                    <Paper key={ev.id} variant="outlined" sx={{ p: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {ev.note && <Typography variant="body2" noWrap>{ev.note}</Typography>}
                        <Typography variant="caption" color="text.secondary">
                          {ev.created_at ? new Date(ev.created_at).toLocaleDateString("en-GB") : ""}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Chip
                          label={ev.confirmed ? "Confirmed" : "Pending"}
                          color={ev.confirmed ? "success" : "default"}
                          size="small"
                        />
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setPreviewUrl(ev.file_url)}
                        >
                          View
                        </Button>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              </StyledPaper>
            </Grid>
          )}

          {/* Add missing fee items */}
          {classroomId && (
            <Grid item xs={12}>
              <StyledPaper>
                <Box sx={{ padding: "12px 15px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography variant="formSubHeading" sx={{ textTransform: "none" }}>
                    Add Missing Fee Items
                  </Typography>
                  {fetchingFees && <CircularProgress size={16} />}
                </Box>
                {!fetchingFees && missingFees.length === 0 && (
                  <Box sx={{ px: 2, pb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      All fee items for this classroom (that apply to this student) are already on this invoice.
                    </Typography>
                  </Box>
                )}
                {missingFees.length > 0 && (
                  <Box sx={{ px: 2, pb: 2 }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 1.5 }}>
                      {missingFees.map((fee) => (
                        <FormControlLabel
                          key={fee.id}
                          control={
                            <Checkbox
                              size="small"
                              checked={selectedFeeIds.includes(fee.id)}
                              onChange={() => toggleFee(fee.id)}
                            />
                          }
                          label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography variant="body2">{fee.name}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                ₦{(fee.amount || 0).toLocaleString()}
                              </Typography>
                              <Chip
                                label={fee.is_compulsory ? "Compulsory" : "Optional"}
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                          }
                        />
                      ))}
                    </Box>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddCircleOutlineIcon />}
                      disabled={addingItems || selectedFeeIds.length === 0}
                      onClick={handleAddItems}
                    >
                      Add Selected ({selectedFeeIds.length})
                    </Button>
                  </Box>
                )}
              </StyledPaper>
            </Grid>
          )}

          {/* Confirm payment section */}
          {!isPaid && (
            <Grid item xs={12}>
              <StyledPaper>
                <Box sx={{ padding: "12px 15px" }}>
                  <Typography variant="formSubHeading" sx={{ textTransform: "none" }}>
                    Confirm Payment
                  </Typography>
                </Box>
                <Box sx={{ p: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                  <TextField
                    size="small"
                    label="Amount Paid (₦)"
                    type="number"
                    value={amountPaid}
                    onChange={(e) => setAmountPaid(e.target.value)}
                    sx={{ width: 180 }}
                    inputProps={{ min: 0 }}
                  />
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleOutlineIcon />}
                    disabled={confirming || !amountPaid}
                    onClick={() => handleConfirm(false)}
                  >
                    Confirm Partial
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<CheckCircleOutlineIcon />}
                    disabled={confirming}
                    onClick={() => handleConfirm(true)}
                  >
                    Confirm Fully Paid
                  </Button>
                </Box>
              </StyledPaper>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <EvidencePreviewModal
        url={previewUrl}
        open={!!previewUrl}
        onClose={() => setPreviewUrl(null)}
      />
    </Dialog>
  );
}

export default InvoiceDialog;
